import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, House, Wrench, VideoCamera, Users, CalendarCheck, ArrowLeft, Warning } from "@phosphor-icons/react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return d; }
};

export default function PropertyBoard() {
  const { address } = useParams();
  const decoded = decodeURIComponent(address || "");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!decoded) return;
    api.get(`/property-board?address=${encodeURIComponent(decoded)}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed"));
  }, [decoded]);

  const t = data?.totals || {};

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="property-board-page">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Link to="/admin" className="inline-flex items-center gap-1 hover:text-cyan-700"><ArrowLeft weight="bold" size={12}/> Dashboard</Link>
          <span>·</span>
          <Link to={`/admin/properties/${encodeURIComponent(decoded)}`} className="hover:text-cyan-700 truncate">Property Snapshot</Link>
        </div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Multi-Unit Board</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
            <Buildings weight="fill" size={28}/> {decoded}
          </h1>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Kpi icon={<House weight="fill" size={16}/>} label="Units"    value={t.unit_count}/>
          <Kpi icon={<Users weight="fill" size={16}/>} label="Occupied" value={`${t.occupied}/${t.unit_count}`} accent="text-emerald-700"/>
          <Kpi icon={<Warning weight="fill" size={16}/>} label="Vacancy" value={`${t.vacancy_pct}%`} accent={t.vacancy_pct > 20 ? "text-red-600" : "text-emerald-700"}/>
          <Kpi icon={<Wrench weight="fill" size={16}/>} label="Open WOs" value={t.open_wo_count} accent={t.open_wo_count > 0 ? "text-amber-700" : ""}/>
          <Kpi icon={<VideoCamera weight="fill" size={16}/>} label="Cameras" value={t.camera_count}/>
        </div>

        {/* Unit grid */}
        {!data && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
        {data && data.units.length === 0 && (
          <div className="p-6 bg-white border-2 border-dashed border-slate-300 text-center text-slate-500 text-sm">No units configured yet.</div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="unit-grid">
          {(data?.units || []).map(u => {
            const borderColor = u.urgent_open > 0 ? "border-red-600"
              : u.occupied ? "border-emerald-600" : "border-slate-400";
            const dateChip = u.move_in_date
              ? { label: `Move-in ${fmtDate(u.move_in_date)}`, className: "bg-emerald-600" }
              : u.move_out_date
              ? { label: `Move-out ${fmtDate(u.move_out_date)}`, className: "bg-amber-600" }
              : null;
            return (
              <Link key={u.unit} to={`/admin/properties/${encodeURIComponent(decoded)}/unit/${encodeURIComponent(u.unit)}`}
                data-testid={`unit-tile-${u.unit}`}
                className={`block p-2 bg-white border-2 ${borderColor} hover:shadow-[4px_4px_0_#0f172a] transition-shadow min-h-[110px]`}>
                <div className="flex items-start justify-between">
                  <div className="font-display font-black text-lg tracking-tight">{u.unit || "—"}</div>
                  {u.occupied && <span className="px-1 py-0.5 text-[8px] font-black uppercase tracking-widest bg-red-600 text-white">OCC</span>}
                </div>
                {dateChip && (
                  <div className={`mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${dateChip.className}`}>
                    {dateChip.label}
                  </div>
                )}
                {u.open_wo_count > 0 && (
                  <div className={`mt-1 text-[10px] font-black uppercase tracking-widest ${u.urgent_open > 0 ? "text-red-700" : "text-amber-700"}`}>
                    {u.urgent_open > 0 && <>🚨 </>}
                    {u.open_wo_count} open WO{u.open_wo_count === 1 ? "" : "s"}
                  </div>
                )}
                {u.open_wo_titles && u.open_wo_titles.slice(0, 2).map(w => (
                  <div key={w.id} className="text-[10px] text-slate-500 truncate">· {w.title}</div>
                ))}
              </Link>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}

function Kpi({ icon, label, value, accent }) {
  return (
    <div className="p-2 bg-white border-2 border-slate-900 shadow-[2px_2px_0_#0f172a]">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`font-display font-black text-2xl tabular-nums ${accent || ""}`}>{value ?? "—"}</div>
    </div>
  );
}
